import React, { useEffect, useRef, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Checkbox, Form, Grid } from 'semantic-ui-react'
import { FormFields } from '~/components/FormFields'
import { formatNumber, handleErrors } from '~/utils/helpers'
import RaceSlider from './RaceSlider'
import InfoPopup from '~/components/InfoPopup'
import ClimaCheckService from '~/api/services/climaCheck'
import { StyledButton, StyledField } from '../style'
import { redistributeCattleNumbers } from '~/pages/Farmer/Co2Climate/AnswerCO2Climate/utils'
import { BLACK_WHITE_COW_ID, BROWN_COW_ID, JERSEY_COW_ID } from '../../utils'

// Assets
import BlackCow from '@assets/images/black-cow.jpg'
import BrowCow from '@assets/images/brown-cow.jpg'
import GrayCow from '@assets/images/gray-cow.jpg'
import BrowWhiteCow from '@assets/images/brown-white-cow.jpg'
import JerseyCow from '@assets/images/jersey-cow.png'
import LinkPopup from '../LinkPopup'
import MilkProduction from './MilkProduction'
import AttritionRate from './AttritionRate'
import WarningIcon from '../WarningIcon'
import ErrorIcon from '../ErrorIcon'
import { FEED_GROUPS } from '../../constants'

interface Props {
  setIsLoadingResults: (value: boolean) => void
  breeds: { id: number, label: string }[]
}

const SecondStep: React.FC<Props> = (props) => {
  const { setIsLoadingResults, breeds } = props
  const { t } = useTranslation()
  const { control, reset, getValues, setValue, clearErrors } = useFormContext()
  const { id, farmerId } = useParams()

  const [isLoadingPredefinedData, setIsLoadingPredefinedData] = useState(false)
  const [defaultValues, setDefaultValues] = useState({
    slaughteredCowWeight: 0,
    lostCowWeight: 0
  })

  const fieldNames = [
    'tax',
    'cowsNumber',
    'dryPeriod',
    'calvingPeriod',
    'slaughterCowWeight',
    'breedingCattleSalesWeight',
    'lostCowWeight',
    'averageLiveWeight',
    'slaughterCowsNumber',
    'cowLosses',
    'breedingCattleSalesNumber'
  ];

  const [
    watchTaxField,
    watchCowsNumber,
    watchDryPeriod,
    watchCalvingPeriod,
    watchCowWeight,
    watchBreedingCattle,
    watchLostCowWeight,
    watchAvgLiveWeight,
    watchSlaughterCowsNumber,
    watchCowLosses,
    watchBreedingCattleNumbers
  ] = useWatch({ control, name: fieldNames }) || [];

  const watchBreedField = useWatch({ control, name: 'breeds' }) || []

  const isFirstRender = useRef(true)

  const values = getValues()
  const {
    milkPrice,
    fat,
    protein,
    totalMilkAmount,
    ureaContent,
    naturalMilkAmount
  } = values

  async function fetchFeedGroups() {
    try {
      const response = await ClimaCheckService.getClimaChPredData(id, { breeds: watchBreedField, tax: watchTaxField, farmerId })
      return response.data.feedGroups?.length > 0 ? response.data.feedGroups : FEED_GROUPS
    } catch (error) {
      handleErrors(error)
      return null
    }
  }

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }

    const recalculate = async () => {
      let feedGroups = getValues('feedGroups')
      const newFeedGroups = []
      if (
        feedGroups &&
        feedGroups?.length > 0 &&
        breeds.length > 0 &&
        watchCalvingPeriod &&
        watchDryPeriod &&
        watchCowsNumber
      ) {
        let indexDryFeedGroup = feedGroups.findIndex(group => group.type === 1 && group.disabled === 1)
        let indexLactFeedGroup = feedGroups.findIndex(group => group.type === 2 && group.disabled === 1)

        if (indexDryFeedGroup < 0 || indexLactFeedGroup < 0) {
          const fetchedFeedGroups = await fetchFeedGroups()
          if (!fetchedFeedGroups) return
          feedGroups = fetchedFeedGroups
          indexDryFeedGroup = feedGroups.findIndex(group => group.type === 1 && group.disabled === 1)
          indexLactFeedGroup = feedGroups.findIndex(group => group.type === 2 && group.disabled === 1)
        }

        if (indexDryFeedGroup < 0 || indexLactFeedGroup < 0) return

        const cattleNumberDryP = parseInt(watchCowsNumber) / parseInt(watchCalvingPeriod) * watchDryPeriod

        newFeedGroups.push({
          ...feedGroups[indexDryFeedGroup],
          days: watchDryPeriod,
          cattleNumber: cattleNumberDryP
        })

        newFeedGroups.push({
          ...feedGroups[indexLactFeedGroup],
          days: watchCalvingPeriod,
          cattleNumber: parseInt(watchCowsNumber) - cattleNumberDryP
        })
        setValue('feedGroups', redistributeCattleNumbers(newFeedGroups, watchCowsNumber))
      }
    }

    recalculate()
  }, [watchCalvingPeriod, watchDryPeriod, watchCowsNumber])

  useEffect(() => {
    if (parseInt(watchSlaughterCowsNumber) === 0) {
      setValue('slaughterCowWeight', 0)
    } else if (parseInt(watchSlaughterCowsNumber) > 0 && parseInt(watchCowWeight) === 0) {
      setValue('slaughterCowWeight', defaultValues.slaughteredCowWeight)
    }

    if (parseInt(watchCowLosses) === 0) {
      setValue('lostCowWeight', 0)
    }

    if (parseInt(watchBreedingCattleNumbers) === 0) {
      setValue('breedingCattleSalesWeight', 0)
    }
  }, [watchSlaughterCowsNumber, watchCowLosses, watchBreedingCattleNumbers])

  async function fetchPredefinedData(value) {
    setIsLoadingPredefinedData(true)
    setIsLoadingResults(true)
    try {
      const formValues = getValues()
      const response = await ClimaCheckService.getClimaChPredData(id, { breeds: value, tax: watchTaxField, farmerId })
      setDefaultValues({
        slaughteredCowWeight: response.data.slaughterCowWeight,
        lostCowWeight: response.data.lostCowWeight
      })

      const calfWeightMap = {
        [BLACK_WHITE_COW_ID]: 70,
        [JERSEY_COW_ID]: 30
      };
      const calfWeightAtFourWeeks = calfWeightMap[value[0].id] ?? formValues?.calfWeightAtFourWeeks

      reset({
        ...formValues,
        ...response.data,
        calfRearing: '',
        cowsNumber: '',
        breeds: value,
        departureRate: '',
        dieselConsumption: '',
        pastureDays: '',
        calvingPeriod: '',
        calvesStillbornAndDead: '',
        lostCowWeight: '630',
        calfWeightAtFourWeeks,
        manureType: '',
        energyConsumptions: [],
        manureStorageTypes: [],
        phaseFeeding: 0,
        feedingPhases: [],
        youngCattleBreedId: value[0].id !== BROWN_COW_ID ? value[0].id : ''
      })
      setValue('breeds', value)
    } catch (error) {
      handleErrors(error)
    }
    setIsLoadingPredefinedData(false)
    setIsLoadingResults(false)
  }

  function firstRow() {
    return <Grid.Row columns={4}>
      <Grid.Column>
        <FormFields.Input
          control={control}
          name={'calvingPeriod'}
          style={{ textAlign: 'center' }}
          typeInput='controled'
          allowDecimals={false}
          withoutAsterisk
          noErrorMessage
          label={<div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <label>{t('Calving interval')}</label>
            <InfoPopup
              content={'Zeitraum zwischen zwei Kalbungen. Standardwert rasseabhängig zwischen 390 und 420 Tagen.'}
              styleIcon={{ fontSize: '1.2em' }}
              position={'top center'}
            />
            <LinkPopup
              content='MLP-Bericht'
            />
            {(watchCalvingPeriod < 330 || watchCalvingPeriod > 450) && <WarningIcon
              control={control}
              name='calvingPeriod'
              content={'Die Zwischenkalbezeit liegt außerhalb des Normalbereichs von mindestens 330 Tagen und maximal 450 Tagen.'}
            />}
            <ErrorIcon
              control={control}
              name={'calvingPeriod'}
            />
          </div>}
          suffix=' Tage'
          rules={{
            validate: (value) => {
              const transformValue = typeof (value) === 'string' ? parseFloat(value.replace(',', '.')) : value
              if (transformValue < 290 || transformValue > 550) {
                return 'Die Zwischenkalbezeit muss mindestens 290 und maximal 550 Tage betragen.'
              }
              return true
            },
            required: true
          }}
          placeholder={t('in days')}
        />
      </Grid.Column>

      <Grid.Column>
        <FormFields.Input
          control={control}
          suffix=' Tage'
          name={'dryPeriod'}
          label={<div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <label>{t('Dry period')}</label>
            <LinkPopup
              content={'Milchleistungsprüfung, Herdenmanagement Programm'}
              position='top center'
            />
            {(watchDryPeriod < 28 || watchDryPeriod > 70) && <WarningIcon
              control={control}
              name='dryPeriod'
              content={'Die eingegebene Trockenstehzeit liegt außerhalb der normalen Bereiche von mind. 28 Tagen und maximal 70 Tagen.'}
            />}
            <ErrorIcon
              control={control}
              name={'dryPeriod'}
            />
          </div>}
          withoutAsterisk
          noErrorMessage
          rules={{
            validate: (value) => {
              const transformValue = typeof (value) === 'string' ? parseFloat(value.replace(',', '.')) : value
              const transformedCalvingPeriod = parseInt(watchCalvingPeriod)
              if (transformValue <= 9) {
                return 'Die Trockenstehzeit kann minimal 9 Tage betragen.'
              }
              if (transformValue <= 30 || transformValue >= transformedCalvingPeriod) {
                return false
              } else if (transformValue > 100) {
                return 'Die Trockenstehzeit kann maximal 100 Tage betragen.'
              }
              return true
            },
            required: true
          }}
          typeInput={'controled'}
          placeholder={'⌀-Trockenstehzeit'}
        />
      </Grid.Column>

      <Grid.Column>
      </Grid.Column>
      <Grid.Column>
      </Grid.Column>
    </Grid.Row>
  }

  function secondRow() {
    return <Grid.Row columns={4} style={{ alignItems: 'end' }}>
      <Grid.Column>
        <FormFields.Input
          control={control}
          name={'cowsNumber'}
          suffix={' Milchkühe'}
          withoutAsterisk
          label={<div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <label>{t('⌀ inventory')}</label>
            <LinkPopup
              content={'HITier (Bestandsregister, "weiblich, mit Abkalbung" zählen)'}
            />
            {watchCowsNumber > 2000 && <WarningIcon
              control={control}
              name='cowsNumber'
              content={'Der ⌀-Bestand scheint sehr hoch zu sein. Sind Sie sicher, dass dieser Wert stimmt?'}
            />}
            <ErrorIcon
              control={control}
              name={'cowsNumber'}
            />
          </div>}
          typeInput='controled'
          defaultValue={0}
          noErrorMessage
          rules={{
            validate: (value) => {
              const transformValue = typeof (value) === 'string' ? parseFloat(value.replace(',', '.')) : value
              if (transformValue < 1) {
                return false
              }
              return true
            },
            required: true
          }}
          placeholder={t('Number of dairy cows')}
        />
      </Grid.Column>

      <Grid.Column>
        <FormFields.Input
          control={control}
          name={'slaughterCowsNumber'}
          styleErrorLabel={{ position: 'absolute', zIndex: 999, left: 20, marginTop: '0.5em' }}
          typeInput='controled'
          noErrorMessage
          withoutAsterisk
          rules={{
            required: true,
            min: 0,
            max: {
              value: watchCowsNumber ? watchCowsNumber * 0.6 : null,
              message: 'Die eingetragene Anzahl an Schlachtkühen beträgt mehr 60% des ⌀-Bestand.'
            }
          }}
          suffix=' Schlachtkühe'
          label={<div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <label>{'Anzahl Schlachtkühe'}</label>
            <InfoPopup
              content={<div dangerouslySetInnerHTML={{ __html: 'Die Emissionen des Fleisches der Schlachtkühe werden dem gesondert ausgewiesenen CO<sub>2</sub>- Fußabdruck „Schlachtkuh“ angerechnet.' }}/>}
              position={'top center'}
              styleIcon={{ fontSize: '1.2em' }}
            />
            <LinkPopup
              content={'HITier, Kennung "SC"'}
            />
            {watchSlaughterCowsNumber > (watchCowsNumber * 0.3) && <WarningIcon
              control={control}
              name='slaughterCowsNumber'
              content={'Die Anzahl der Schlachtkühe liegt 30% über Ihrem ⌀-Bestand. Dieser Wert erscheint sehr hoch.'}
            />}
            <ErrorIcon
              control={control}
              name={'slaughterCowsNumber'}
            />
          </div>}
          placeholder={t('Anzahl Schlachtkühe')}
        />
      </Grid.Column>

      <Grid.Column>
        <FormFields.Input
          control={control}
          name={'cowLosses'}
          suffix={' Kuhverluste'}
          withoutAsterisk
          label={<div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <label>{'Kuhverluste (Verendungen, Tötungen)'}</label>
            <InfoPopup
              content={'Wie viele Milchkühe sind verendet oder wurden notgetötet?'}
              position={'top center'}
              styleIcon={{ fontSize: '1.2em' }}
            />
            <LinkPopup
              content={'HITier, Kennung "VE" und "TOE"'}
            />
            <ErrorIcon
              control={control}
              name={'cowLosses'}
            />
          </div>}
          typeInput='controled'
          noErrorMessage
          rules={{
            required: true,
            min: 0,
            validate: (value) => {
              const transformValue = typeof (value) === 'string' ? parseFloat(value.replace(',', '.')) : value
              if (watchCowsNumber && transformValue > parseInt(watchCowsNumber)) {
                return 'Die Anzahl der Kuhverluste darf nicht größer als der ⌀-Bestand sein.'
              }
              return true
            }
          }}
          placeholder={'Anzahl Kuhverluste'}
        />
      </Grid.Column>
      <Grid.Column>
        <FormFields.Input
          control={control}
          name={'breedingCattleSalesNumber'}
          suffix={' Zuchtviehverkäufe'}
          withoutAsterisk
          label={<div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <label>{'Zuchtviehverkäufe'}</label>
            <InfoPopup
              content={'Wie viele Milchkühe haben Sie als Zuchtvieh verkauft?'}
              position={'top center'}
              styleIcon={{ fontSize: '1.2em' }}
            />
            <LinkPopup
              content='HITier, Herdenmanagementprogramm, BZA'
            />
            <ErrorIcon
              control={control}
              name={'breedingCattleSalesNumber'}
            />
          </div>}
          typeInput='controled'
          noErrorMessage
          rules={{
            required: true,
            min: 0,
            validate: (value) => {
              const transformValue = typeof (value) === 'string' ? parseFloat(value.replace(',', '.')) : value
              if (watchCowsNumber && transformValue > parseInt(watchCowsNumber)) {
                return 'Die Anzahl der Zuchtviehverkäufe darf nicht größer als der ⌀-Bestand sein.'
              }
              return true
            }
          }}
          placeholder={'Anzahl Zuchtviehverkäufe'}
        />
      </Grid.Column>
    </Grid.Row>
  }

  function thirdRow() {
    const minOldCowWeight = watchAvgLiveWeight ? parseInt(watchAvgLiveWeight) - 200 : 0
    const maxOldCowWeight = watchAvgLiveWeight ? parseInt(watchAvgLiveWeight) + 200 : 200
    return <Grid.Row columns={4}>
      <Grid.Column>
        <StyledField>
          <label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <div>{t('Ø live weight')}</div>
              <InfoPopup
                content={'Wie viel wogen Ihre Milchkühe im Durchschnitt? Der vorgeschlagene Wert wurde anhand der angegebenen Rasse berechnet.'}
                position={'top center'}
                styleIcon={{ fontSize: '1.2em' }}
              />
              {(watchAvgLiveWeight < 400 || watchAvgLiveWeight > 900) && <WarningIcon
                control={control}
                name='averageLiveWeight'
                content={'Das ⌀-Lebendgewicht liegt außerhalb des Normalbereichs von mindestens 400 kg und maximal 900 kg.'}
              />}
              <ErrorIcon
                control={control}
                name={'averageLiveWeight'}
              />
            </div>
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 15, marginTop: 8 }}>
            <StyledButton
              type='button'
              icon='minus'
              disabled={watchAvgLiveWeight - 10 <= 0 || !watchAvgLiveWeight}
              onClick={() => {
                if (!watchAvgLiveWeight) {
                  return
                }
                const numberLiveWeight = parseInt(watchAvgLiveWeight) - 10
                if (numberLiveWeight >= 350 && numberLiveWeight <= 900) {
                  clearErrors('averageLiveWeight')
                }
                setValue('averageLiveWeight', numberLiveWeight)
              }}
            />
            <FormFields.Input
              control={control}
              name={'averageLiveWeight'}
              noErrorMessage
              typeInput='controled'
              withoutAsterisk
              rules={{
                validate: (value) => {
                  const transformValue = typeof (value) === 'string' ? parseFloat(value.replace(',', '.')) : value
                  if (transformValue < 100 || transformValue > 900) {
                    return 'Das ⌀-Lebendgewicht muss mindestens 100 kg und darf maximal 900 kg pro Kuh betragen.'
                  }
                  return true
                },
                required: true
              }}
              styleErrorLabel={{ position: 'absolute', zIndex: 999, left: 20, marginTop: '0.5em' }}
              suffix=' kg pro Kuh'
              placeholder={t('in kg pro Kuh')}
            />
            <StyledButton
              type='button'
              onClick={() => {
                const numberLiveWeight = parseInt(watchAvgLiveWeight || 0) + 10
                if (numberLiveWeight >= 350 && numberLiveWeight <= 950) {
                  clearErrors('averageLiveWeight')
                }
                setValue('averageLiveWeight', numberLiveWeight)
              }}
              icon='add'
            />
          </div>
        </StyledField>
      </Grid.Column>

      <Grid.Column>
        <StyledField>
          <label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <div>{t('Old cow slaughter weight')}</div>
              <InfoPopup
                content={'Zur korrekten Berechnung wird ein Schlachtkörper-/Karkassengewicht benötigt'}
                position={'top center'}
                styleIcon={{ fontSize: '1.2em' }}
              />
              <LinkPopup
                content='Schlachtabrechnungen, Wiegeergebnisse'
              />
              {(watchCowWeight < 200 || watchCowWeight > 600) && <WarningIcon
                control={control}
                name='slaughterCowWeight'
                content={'Das ⌀-Schlachtkörpergewicht liegt außerhalb des Normalbereichs von mindestens 200 kg und maximal 600 kg.'}
              />}
              <ErrorIcon
                control={control}
                name={'slaughterCowWeight'}
              />
            </div>
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 15, marginTop: 8 }}>
            <StyledButton
              type='button'
              icon='minus'
              disabled={(watchCowWeight - 10 <= 0 || !watchCowWeight) || (parseInt(watchSlaughterCowsNumber) === 0)}
              onClick={() => {
                if (!watchCowWeight) {
                  return
                }
                const numerSlaughtered = parseInt(watchCowWeight) - 10
                if (numerSlaughtered >= 150 && numerSlaughtered <= 550) {
                  clearErrors('slaughterCowWeight')
                }
                setValue('slaughterCowWeight', numerSlaughtered)
              }}
            />
            <FormFields.Input
              control={control}
              typeInput='controled'
              styleErrorLabel={{ position: 'absolute', zIndex: 999, left: 20, marginTop: '0.5em' }}
              withoutAsterisk
              rules={{
                validate: (value) => {
                  if (!watchSlaughterCowsNumber || parseInt(watchSlaughterCowsNumber) === 0) {
                    return true
                  }
                  const transformValue = typeof (value) === 'string' ? parseFloat(value.replace(',', '.')) : value
                  if (transformValue < 100 || transformValue > 550) {
                    return 'Das ⌀-Schlachtkörpergewicht muss mindestens 100 kg und darf maximal 550 kg pro Kuh betragen.'
                  }
                  return true
                },
                required: true
              }}
              suffix=' kg pro Altkuh'
              disabled={(parseInt(watchSlaughterCowsNumber) === 0)}
              noErrorMessage
              name={'slaughterCowWeight'}
              placeholder={t('in kg pro Kuh')}
            />
            <StyledButton
              type='button'
              disabled={(parseInt(watchSlaughterCowsNumber) === 0)}
              onClick={() => {
                const numerSlaughtered = parseInt(watchCowWeight || 0) + 10
                if (numerSlaughtered >= 150 && numerSlaughtered <= 550) {
                  clearErrors('slaughterCowWeight')
                }
                setValue('slaughterCowWeight', numerSlaughtered)
              }}
              icon='add'
            />
          </div>
        </StyledField>
      </Grid.Column>

      <Grid.Column>
        <StyledField>
          <label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <div>{'⌀-Gewicht der Kuhverluste'}</div>
              <InfoPopup
                content={'Wenn diese Tiere krank waren, wiegen sie meist weniger als der Herdendurchschnitt.'}
                styleIcon={{ fontSize: '1.2em' }}
                position={'top center'}
              />
              {(watchLostCowWeight < 400 || watchLostCowWeight > 900) && parseInt(watchCowLosses) > 0 && <WarningIcon
                control={control}
                name='lostCowWeight'
                content={'Das Gewicht der Kuhverluste liegt außerhalb des Normalbereichs von mindestens 400 kg und maximal 900 kg.'}
              />}
              <ErrorIcon
                control={control}
                name={'lostCowWeight'}
              />
            </div>
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 15, marginTop: 8 }}>
            <StyledButton
              type='button'
              icon='minus'
              disabled={(watchLostCowWeight - 10 <= 0 || !watchLostCowWeight) || parseInt(watchCowLosses) === 0}
              onClick={() => {
                if (!watchLostCowWeight) {
                  return
                }
                const numberLostCowWeight = parseInt(watchLostCowWeight) - 10
                if (numberLostCowWeight >= 100 && numberLostCowWeight <= 1500) {
                  clearErrors('lostCowWeight')
                }
                setValue('lostCowWeight', numberLostCowWeight)
              }}
            />
            <FormFields.Input
              control={control}
              name={'lostCowWeight'}
              suffix={' kg pro Kuh'}
              withoutAsterisk
              typeInput='controled'
              disabled={parseInt(watchCowLosses) === 0}
              noErrorMessage
              rules={{
                validate: (value) => {
                  if (parseInt(watchCowLosses) === 0) {
                    return
                  }
                  const transformValue = typeof (value) === 'string' ? parseFloat(value.replace(',', '.')) : value
                  if (transformValue < 100 || transformValue > 1500) {
                    return false
                  }
                  return true
                },
                required: true
              }}
              placeholder={'in kg pro Kuh'}
            />
            <StyledButton
              type='button'
              disabled={parseInt(watchCowLosses) === 0}
              onClick={() => {
                const numberLostCowWeight = parseInt(watchLostCowWeight || 0) + 10
                if (numberLostCowWeight >= 100 && numberLostCowWeight <= 1500) {
                  clearErrors('lostCowWeight')
                }
                setValue('lostCowWeight', numberLostCowWeight)
              }}
              icon='add'
            />
          </div>
        </StyledField>
      </Grid.Column>

      <Grid.Column>
        <StyledField>
          <label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <div>{'⌀-Gewicht Zuchtvieh'}</div>
              <InfoPopup
                content={'Wie viel wogen die als Zuchtvieh verkauften Milchkühe im Durchschnitt?'}
                position={'top center'}
                styleIcon={{ fontSize: '1.2em' }}
              />
              {(watchBreedingCattle < 300 || watchBreedingCattle > 800) && parseInt(watchBreedingCattleNumbers) > 0 && <WarningIcon
                control={control}
                name='breedingCattleSalesWeight'
                content={'Das Gewicht der Zuchtviehverkäufe liegt außerhalb des Normalbereichs von mindestens 300 kg und maximal 800 kg.'}
              />}
              <ErrorIcon
                control={control}
                name={'breedingCattleSalesWeight'}
              />
            </div>
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 15, marginTop: 8 }}>
            <StyledButton
              type='button'
              icon='minus'
              disabled={(watchBreedingCattle - 10 <= 0 || !watchBreedingCattle) || parseInt(watchBreedingCattleNumbers) === 0}
              onClick={() => {
                if (!watchBreedingCattle || parseInt(watchBreedingCattleNumbers) === 0) {
                  return
                }
                const numberBreedCattle = parseInt(watchBreedingCattle) - 10
                if (numberBreedCattle >= 100 && numberBreedCattle <= 800) {
                  clearErrors('breedingCattleSalesWeight')
                }
                setValue('breedingCattleSalesWeight', numberBreedCattle)
              }}
            />
            <FormFields.Input
              control={control}
              disabled={parseInt(watchBreedingCattleNumbers) === 0}
              name={'breedingCattleSalesWeight'}
              noErrorMessage={!watchBreedingCattle}
              suffix={' kg Lebendmasse'}
              styleErrorLabel={{ position: 'absolute', zIndex: 999, left: 20, marginTop: '0.5em' }}
              withoutAsterisk
              typeInput='controled'
              rules={{
                min: {
                  value: parseInt(watchBreedingCattleNumbers) === 0 ? 0 : minOldCowWeight,
                  message: `Ihr Ø-Schlachtgewicht (${watchBreedingCattle} kg) muss innerhalb +/- 200kg des ⌀-Lebendgewicht (${watchAvgLiveWeight} kg) liegen.`
                },
                max: {
                  value: parseInt(watchBreedingCattleNumbers) === 0 ? 0 : maxOldCowWeight,
                  message: `Ihr Ø-Schlachtgewicht (${watchBreedingCattle} kg) muss innerhalb +/- 200kg des ⌀-Lebendgewicht (${watchAvgLiveWeight} kg) liegen.`
                },
                required: true
              }}
              placeholder={'in kg Lebendmasse'}
            />
            <StyledButton
              type='button'
              disabled={parseInt(watchBreedingCattleNumbers) === 0}
              onClick={() => {
                const numberBreedCattle = parseInt(watchBreedingCattle || 0) + 10
                if (numberBreedCattle >= 100 && numberBreedCattle <= 800) {
                  clearErrors('breedingCattleSalesWeight')
                }
                setValue('breedingCattleSalesWeight', numberBreedCattle)
              }}
              icon='add'
            />
          </div>
        </StyledField>
      </Grid.Column>

    </Grid.Row>
  }

  function fourthRow() {
    return <Grid.Row columns={4}>
      <Grid.Column>
        <MilkProduction />
      </Grid.Column>

      <Grid.Column>
      </Grid.Column>
      <Grid.Column>
      </Grid.Column>
      <Grid.Column>
        <AttritionRate />
      </Grid.Column>
    </Grid.Row>
  }

  function renderFields() {
    return <div style={{ marginTop: '2em' }}>
      <Grid columns={3}>
        {firstRow()}
        {secondRow()}
        {thirdRow()}
        {fourthRow()}
      </Grid>
    </div>
  }

  function getSource(breed) {
    switch (breed.id) {
      case 1:
        return BrowWhiteCow
      case 2:
        return GrayCow
      case 3:
        return BrowCow
      case 4:
        return BlackCow
      case 5:
        return JerseyCow
    }
  }

  function renderRaceSlider() {
    return <div style={{ position: 'absolute', left: 31, top: 1 }}>
      <RaceSlider />
    </div>
  }

  function renderRace() {
    return <Form.Field>
      <label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div>{'Hauptrasse'}</div>
          <InfoPopup
            content={`Nach der Auswahl der Hauptrasse haben Sie die Möglichkeit eine zweite Rasse mitsamt ihres prozentualen Anteils auszuwählen. 
              Für die Berechnung kann jedoch lediglich die Hauptrasse berücksichtigt werden.`}
            styleIcon={{ fontSize: '1.2em' }}
            position={'top center'}
          />
        </div>
      </label>
      <Form.Group widths={'equal'} style={{ alignItems: 'center', marginTop: '2em' }}>
        {breeds?.map((breed) => {
          const index = watchBreedField?.findIndex(breedSelected => breedSelected.id === breed.id)
          const maxChecked = watchBreedField?.length === 2
          const displayPercent = maxChecked && index >= 0

          const addOpacity = watchBreedField && watchBreedField?.length > 0 && index < 0
          return <Form.Field key={breed.id} style={{ position: 'relative', opacity: addOpacity ? '0.5' : 1 }}>
            <Checkbox
              label={
                <label style={{ whiteSpace: 'nowrap', minWidth: 200 }}>
                  <img
                    src={getSource(breed)}
                    style={{
                      width: breed.id === 5 ? 80 : 100,
                      marginRight: 10,
                      height: 100,
                      marginTop: maxChecked && index === 1 ? 26 : 20
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      left: 70,
                      display: 'flex',
                      top: index > 0 ? '-22px' : 0,
                      minWidth: 200
                    }}>
                    <div>{`${breed.label} ${displayPercent ? `(${watchBreedField[index].percent} %)` : ''}`}</div>
                  </div>

                </label>
              }
              disabled={isLoadingPredefinedData || (maxChecked && index < 0)}
              checked={index >= 0}
              onChange={(e, data) => {
                // setting main race
                if (data.checked && watchBreedField.length === 0) {
                  fetchPredefinedData([...watchBreedField, {
                    ...breed,
                    percent: watchBreedField.length === 0 ? '100' : ''
                  }])
                  // setting second race
                } else if (data.checked && watchBreedField.length > 0) {
                  setValue('breeds', [...watchBreedField, {
                    ...breed,
                    percent: '0'
                  }])
                  //deselecting main race while second checked
                } else if (watchBreedField.length > 0 && index === 0) {
                  reset({
                    milkPrice,
                    fat,
                    protein,
                    totalMilkAmount,
                    ureaContent,
                    naturalMilkAmount,
                    breeds: []
                  })
                  //deselecting second reace checked
                } else {
                  const tempBreeds = watchBreedField.filter(breedSelected => breedSelected.id !== breed.id)
                  if (tempBreeds[0]) {
                    tempBreeds[0].percent = '100'
                  }
                  setValue('breeds', tempBreeds)
                }
              }}
            />
            {maxChecked && index === 1 && renderRaceSlider()}
          </Form.Field>
        })}
      </Form.Group>
    </Form.Field>
  }

  return <>
    {renderRace()}
    {watchBreedField?.length > 0 && !isLoadingPredefinedData && renderFields()}
  </>
}
export default SecondStep